import { parseAppointmentStatus } from './enumParsers';
import type { CalendarAbsenceDto, CalendarAppointmentDto } from '../types/api-responses';

/**
 * AB#2023 (CR-2026-07-19-v3) — client-side staff availability, so the calendar
 * can tell whoever takes a booking call, at a glance, whether a staff member is
 * free at a given time (and if not, why + when they are next free). Computed
 * purely from data the calendar grid already holds — per-day shift window,
 * absences (breaks + whole-day leave), and appointments — with NO backend call.
 */

const hhmmToMin = (t?: string | null): number | null => {
    if (!t || !t.includes(':')) return null;
    const [h, m] = t.split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    return h * 60 + m;
};

export const minToHhmm = (m: number): string =>
    `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

// Appointment statuses that occupy the chair (block the slot). Cancelled(4) and
// NoShow(5) do not. Mirrors the BE booking guard (+ Completed, which still
// consumed the time). See parseAppointmentStatus for the string↔int mapping.
const OCCUPYING = new Set([1, 2, 3, 6, 7]);

export type StaffAvailability =
    | { kind: 'off' }                                   // not scheduled that day
    | { kind: 'leave'; typeName: string }               // whole-day Vacation/Sick
    | { kind: 'available' }                             // free at the reference time
    | { kind: 'break'; backAtMin: number }              // an intra-day break covers T
    | { kind: 'booked'; nextFreeMin: number | null }    // an appointment covers T
    | { kind: 'offHours' }                              // T is outside the shift window
    | { kind: 'working'; startMin: number; endMin: number }; // no reference time → day summary

export interface AvailabilityInput {
    shiftStart?: string | null;
    shiftEnd?: string | null;
    /** Absences for THIS staff on this day. */
    absences: CalendarAbsenceDto[];
    /** Appointments for THIS staff on this day. */
    appointments: CalendarAppointmentDto[];
    /** "HH:mm" to evaluate at, or null for a day-level summary (no specific time). */
    referenceTime: string | null;
}

interface Interval { start: number; end: number; }

function busyIntervals(inp: AvailabilityInput): Interval[] {
    const out: Interval[] = [];
    for (const ab of inp.absences) {
        if (ab.isWholeDay) continue;
        const s = hhmmToMin(ab.startTime), e = hhmmToMin(ab.endTime);
        if (s != null && e != null) out.push({ start: s, end: e });
    }
    for (const ap of inp.appointments) {
        const st = parseAppointmentStatus(ap.status);
        if (st == null || !OCCUPYING.has(st)) continue;
        const s = hhmmToMin(ap.startTime), e = hhmmToMin(ap.endTime);
        if (s != null && e != null) out.push({ start: s, end: e });
    }
    return out;
}

/** Earliest minute ≥ `from` (within the shift) at which the chair is free, or null. */
function nextFree(from: number, shiftEnd: number, busy: Interval[]): number | null {
    const sorted = [...busy].sort((a, b) => a.start - b.start);
    let cursor = from;
    for (const iv of sorted) {
        if (iv.end <= cursor) continue;
        if (iv.start > cursor) break;   // a gap opens at `cursor`
        cursor = Math.max(cursor, iv.end);
    }
    return cursor < shiftEnd ? cursor : null;
}

export function computeStaffAvailability(inp: AvailabilityInput): StaffAvailability {
    const shiftS = hhmmToMin(inp.shiftStart), shiftE = hhmmToMin(inp.shiftEnd);
    if (shiftS == null || shiftE == null) return { kind: 'off' };

    const wholeDay = inp.absences.find(a => a.isWholeDay);
    if (wholeDay) return { kind: 'leave', typeName: wholeDay.absenceType };

    const t = hhmmToMin(inp.referenceTime);
    if (t == null) return { kind: 'working', startMin: shiftS, endMin: shiftE };

    if (t < shiftS || t >= shiftE) return { kind: 'offHours' };

    const brk = inp.absences.find(a => {
        if (a.isWholeDay) return false;
        const s = hhmmToMin(a.startTime), e = hhmmToMin(a.endTime);
        return s != null && e != null && s <= t && t < e;
    });
    if (brk) return { kind: 'break', backAtMin: hhmmToMin(brk.endTime)! };

    const busy = busyIntervals(inp);
    if (busy.some(iv => iv.start <= t && t < iv.end)) {
        return { kind: 'booked', nextFreeMin: nextFree(t, shiftE, busy) };
    }
    return { kind: 'available' };
}

export type ReasonKind = 'break' | 'leave' | 'booked' | 'off' | null;

export interface FreeAtResult { free: boolean; reason: ReasonKind; }

/** Boolean "is the chair free at the reference time" + a reason when it isn't. */
export function isFreeAt(inp: AvailabilityInput): FreeAtResult {
    const st = computeStaffAvailability(inp);
    switch (st.kind) {
        case 'available':
        case 'working':
            return { free: true, reason: null };
        case 'off':
        case 'offHours':
            return { free: false, reason: 'off' };
        case 'break':
            return { free: false, reason: 'break' };
        case 'leave':
            return { free: false, reason: 'leave' };
        case 'booked':
            return { free: false, reason: 'booked' };
    }
}
